import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, addDays } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select, 
    Tooltip,     
} from '@mui/material';
import { 
    countLoadFactorOverall,
    countLoadFactorByField,
    countPaxByField,
    countLoadFactorBy2Fields,
} from './utils';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { 
    LAYOUT_HEIGHT, 
    DIVERT_RETURN_STATUS, 
    EXCLUDED_FLIGHT_STATUS,
    SERVICE_TYPE_EXCLUDE, 
} from 'state/constants';
import { 
    removeItemByValueImmutable, 
    parseUTCDate, 
    filterFlights, 
} from 'state/helpers';
import useAuthCheck from 'hooks/useAuthCheck';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import NumberControl from 'components/NumberControl';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import PieChart from 'components/chartNivo/PieChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import TimeRangeChart from 'components/chartNivo/TimeRangeChart';
import { boxStyles as styles } from 'components/CustomStyles';
import LoadFactorTableau from './LoadFactorTableau';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const viewOptions = {
    chart: [
        { value: "lfOverall", label: "Overview" },
        { value: "lfByDate", label: "by Date" },
        { value: "lfByPort", label: "by Port" },
        { value: "lfBySector", label: "by Sector" },
        { value: "lfBySectorType", label: "by Sector Type" },
        { value: "lfByFlightType", label: "by Flight Type" },
        { value: "lfByFlightReg", label: "by Flight Reg" },
        { value: "lfByACType", label: "by AC Type" },
        { value: "lfByCountry", label: "by Country" },
        { value: "lfByFlightNo", label: "by Flight No" },
    ],
    tableau: [
        { value: "lfByDate", label: "by Date" },
        { value: "lfByDateDep", label: "by Date & Port" },
        { value: "lfByDateSector", label: "by Date & Sector" },
        { value: "lfByDateSectorType", label: "by Date & Sector Type" },
        { value: "lfByDateFlightType", label: "by Date & Flight Type" },
        { value: "lfByDateFlightReg", label: "by Date & Flight Reg" },
        { value: "lfByDateACType", label: "by Date & AC Type" },
        { value: "lfByDateCountry", label: "by Date & Country" },
        { value: "lfByDateFlightNo", label: "by Date & Flight No" },

        { value: "lfByDep", label: "by Port" },
        { value: "lfBySector", label: "by Sector" },
        { value: "lfBySectorType", label: "by Sector Type" },
        { value: "lfByFlightType", label: "by Flight Type" },
        
        { value: "lfByFlightReg", label: "by Flight Reg" },
        { value: "lfByFlightRegDep", label: "by Flight Reg & Port" },
        { value: "lfByFlightRegSector", label: "by Flight Reg & Sector" },
        { value: "lfByFlightRegSectorType", label: "by Flight Reg & Sector Type" },
        { value: "lfByFlightRegFlightType", label: "by Flight Reg & Flight Type" },
        { value: "lfByFlightRegCountry", label: "by Flight Reg & Country" },
        
        { value: "lfByACType", label: "by AC Type" },
        { value: "lfByACTypeDep", label: "by AC Type & Port" },
        { value: "lfByACTypeSector", label: "by AC Type & Sector" },
        { value: "lfByACTypeSectorType", label: "by AC Type & Sector Type" },
        { value: "lfByACTypeFlightType", label: "by AC Type & Flight Type" },
        { value: "lfByACTypeCountry", label: "by AC Type & Country" },

        { value: "lfByCountry", label: "by Country" },
        
        { value: "lfByFlightNo", label: "by Flight No" },
    ],
};

const LoadFactor = () => {
    useAuthCheck();    
    const flightsData = useSelector((state) => state.flights);     
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();    
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [minimumPax, setMinimumPax] = useState(0);
    
    const [dataType, setDataType] = useState('actual');
    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState(viewOptions["chart"][0].value);
    const [flights, setFlights] = useState([]); 
    const [data, setData] = useState({        
        lfOverallPie: [],
        lfOverall: [],

        lfByDate: [],     
        paxByDate: [],        
        lfTimeRangeByDate: [],  

        lfByDep: [],
        paxByDep: [],

        lfBySector: [],        
        paxBySector: [],    

        lfBySectorType: [], 
        paxBySectorType: [], 

        lfByFlightType: [],
        paxByFlightType: [],

        lfByFlightReg: [],
        paxByFlightReg: [],

        lfByACType: [],
        paxByACType: [],

        lfByCountry: [],
        paxByCountry: [],

        lfByFlightNo: [],
        paxByFlightNo: [],

        tableauLfByDate: [],
        tableauLfByDateDep: [], 
        tableauLfByDateSector: [], 
        tableauLfByDateSectorType: [], 
        tableauLfByDateFlightType: [], 
        tableauLfByDateFlightReg: [], 
        tableauLfByDateACType: [], 
        tableauLfByDateCountry: [], 
        tableauLfByDateFlightNo: [], 

        tableauLfByDep: [], 
        tableauLfBySector: [], 
        tableauLfBySectorType: [], 
        tableauLfByFlightType: [],

        tableauLfByFlightReg: [], 
        tableauLfByFlightRegDep: [], 
        tableauLfByFlightRegSector: [], 
        tableauLfByFlightRegSectorType: [], 
        tableauLfByFlightRegFlightType: [], 
        tableauLfByFlightRegCountry: [], 

        tableauLfByACType: [],
        tableauLfByACTypeDep: [], 
        tableauLfByACTypeSector: [], 
        tableauLfByACTypeSectorType: [], 
        tableauLfByACTypeFlightType: [], 
        tableauLfByACTypeCountry: [], 

        tableauLfByCountry: [],
        tableauLfByFlightNo: [], 
    });


    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;

        const isValid = options.some(opt => opt.value === view);

        if (!isValid) {
            setView(options[0].value); 
        }
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view)
            ? view
            : options[0]?.value || '';
    }, [view, displayType]);

    useEffect(() => {        
        const handleFlights = async () => {
            try {            
                setLoading(true);
                const processedFlights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
                    .filter(flight => 
                        !DIVERT_RETURN_STATUS.includes(flight.flightStatus) &&
                        !SERVICE_TYPE_EXCLUDE.includes(flight.flightType)
                    );
                setFlights(processedFlights);                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };        
        handleFlights();      
    }, [flightsData]); 

    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);        
    
    useEffect(() => {        
        const flightDate = timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase';  
        const results = {    
            lfOverallPie: countLoadFactorOverall(filteredFlights, dataType, minimumPax, 'pie'),
            lfOverall: countLoadFactorOverall(filteredFlights, dataType, minimumPax),
            
            lfByDate: countLoadFactorByField(filteredFlights, flightDate, dataType, minimumPax),                
            lfTimeRangeByDate: countLoadFactorByField(filteredFlights, flightDate, dataType, minimumPax, 'timerange'),                
            paxByDate: countPaxByField(filteredFlights, flightDate, dataType, minimumPax),                
           
            lfByDep: countLoadFactorByField(filteredFlights, 'dep', dataType, minimumPax),                
            paxByDep: countPaxByField(filteredFlights, 'dep', dataType, minimumPax),                
            
            lfBySector: countLoadFactorByField(filteredFlights, 'sector', dataType, minimumPax),                
            paxBySector: countPaxByField(filteredFlights, 'sector', dataType, minimumPax),                
            
            lfBySectorType: countLoadFactorByField(filteredFlights, 'sectorType', dataType, minimumPax),                
            paxBySectorType: countPaxByField(filteredFlights, 'sectorType', dataType, minimumPax),                
            
            lfByFlightType: countLoadFactorByField(filteredFlights, 'flightType', dataType, minimumPax),                
            paxByFlightType: countPaxByField(filteredFlights, 'flightType', dataType, minimumPax),                
            
            lfByFlightReg: countLoadFactorByField(filteredFlights, 'flightReg', dataType, minimumPax),                
            paxByFlightReg: countPaxByField(filteredFlights, 'flightReg', dataType, minimumPax),                
            
            lfByACType: countLoadFactorByField(filteredFlights, 'acType', dataType, minimumPax),                
            paxByACType: countPaxByField(filteredFlights, 'acType', dataType, minimumPax),     
            
            lfByCountry: countLoadFactorByField(filteredFlights, 'depCountryName', dataType, minimumPax),                
            paxByCountry: countPaxByField(filteredFlights, 'depCountryName', dataType, minimumPax),   
        
            lfByFlightNo: countLoadFactorByField(filteredFlights, 'flightNo', dataType, minimumPax),                
            paxByFlightNo: countPaxByField(filteredFlights, 'flightNo', dataType, minimumPax),                
                        
            tableauLfByDate: countLoadFactorBy2Fields(filteredFlights, flightDate, null, dataType, minimumPax),  
            tableauLfByDateDep: countLoadFactorBy2Fields(filteredFlights, flightDate, 'dep', dataType, minimumPax),  
            tableauLfByDateSector: countLoadFactorBy2Fields(filteredFlights, flightDate, 'sector', dataType, minimumPax),
            tableauLfByDateSectorType: countLoadFactorBy2Fields(filteredFlights, flightDate, 'sectorType', dataType, minimumPax),
            tableauLfByDateFlightType: countLoadFactorBy2Fields(filteredFlights, flightDate, 'flightType', dataType, minimumPax),
            tableauLfByDateFlightReg: countLoadFactorBy2Fields(filteredFlights, flightDate, 'flightReg', dataType, minimumPax),
            tableauLfByDateACType: countLoadFactorBy2Fields(filteredFlights, flightDate, 'acType', dataType, minimumPax),
            tableauLfByDateCountry: countLoadFactorBy2Fields(filteredFlights, flightDate, 'depCountryName', dataType, minimumPax),
            tableauLfByDateFlightNo: countLoadFactorBy2Fields(filteredFlights, flightDate, 'flightNo', dataType, minimumPax),

            tableauLfByDep: countLoadFactorBy2Fields(filteredFlights, 'dep', null, dataType, minimumPax),
            tableauLfBySector: countLoadFactorBy2Fields(filteredFlights, 'sector', null, dataType, minimumPax),
            tableauLfBySectorType: countLoadFactorBy2Fields(filteredFlights, 'sectorType', null, dataType, minimumPax),
            tableauLfByFlightType: countLoadFactorBy2Fields(filteredFlights, 'flightType', null, dataType, minimumPax),
            
            tableauLfByFlightReg: countLoadFactorBy2Fields(filteredFlights, 'flightReg', null, dataType, minimumPax),
            tableauLfByFlightRegDep: countLoadFactorBy2Fields(filteredFlights, 'flightReg', 'dep', dataType, minimumPax),
            tableauLfByFlightRegSector: countLoadFactorBy2Fields(filteredFlights, 'flightReg', 'sector', dataType, minimumPax),
            tableauLfByFlightRegSectorType: countLoadFactorBy2Fields(filteredFlights, 'flightReg', 'sectorType', dataType, minimumPax),
            tableauLfByFlightRegFlightType: countLoadFactorBy2Fields(filteredFlights, 'flightReg', 'flightType', dataType, minimumPax),
            tableauLfByFlightRegCountry: countLoadFactorBy2Fields(filteredFlights, 'flightReg', 'depCountryName', dataType, minimumPax),
            
            tableauLfByACType: countLoadFactorBy2Fields(filteredFlights, 'acType', null, dataType, minimumPax),
            tableauLfByACTypeDep: countLoadFactorBy2Fields(filteredFlights, 'acType', 'dep', dataType, minimumPax),
            tableauLfByACTypeSector: countLoadFactorBy2Fields(filteredFlights, 'acType', 'sector', dataType, minimumPax),
            tableauLfByACTypeSectorType: countLoadFactorBy2Fields(filteredFlights, 'acType', 'sectorType', dataType, minimumPax), 
            tableauLfByACTypeFlightType: countLoadFactorBy2Fields(filteredFlights, 'acType', 'flightType', dataType, minimumPax), 
            tableauLfByACTypeCountry: countLoadFactorBy2Fields(filteredFlights, 'acType', 'depCountryName', dataType, minimumPax),
            
            tableauLfByCountry: countLoadFactorBy2Fields(filteredFlights, 'depCountryName', null, dataType, minimumPax),
            
            tableauLfByFlightNo: countLoadFactorBy2Fields(filteredFlights, 'flightNo', null, dataType, minimumPax),
        };       
                  
        setData(results);  
    }, [timeMode, filteredFlights, dataType, minimumPax]); 
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]); 

    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Load Factor ${period}`;
    const paxTitle = `No. Of Pax ${period}`;
    const fromDate = startDate && format(parseUTCDate(addDays(startDate, -1)), 'yyyy-MM-dd');
    const toDate = endDate && format(parseUTCDate(endDate), 'yyyy-MM-dd');
        
    const chartMap = {
        chart: {
            lfOverall: [
                { component: <PieChart data={data.lfOverallPie} isTotalVisible = {false} title={defaultTitle} />, style: {} },
                { component: <SortBarChart data={data.lfOverall} title={defaultTitle} bottomLegend="Seats" leftLegend="No. Of Pax" isShowAvgLine={false} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
            ],
            lfByDate: [
                { component: <SortBarMultiLinesChart data={data.lfByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Load Factor (%)" digit={0} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <TimeRangeChart data={data.lfTimeRangeByDate} fromDate={fromDate} toDate={toDate} title={defaultTitle} digit={0} itemWidthOffSet={isMobile ? -65 : -60} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByDate} title={paxTitle} bottomLegend="Date" leftLegend="No. Of Pax" digit={0} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
            lfByPort: [
                { component: <SortBarMultiLinesChart data={data.lfByDep} title={defaultTitle} bottomLegend="Port" leftLegend="Load Factor (%)" digit={0} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByDep} title={paxTitle} bottomLegend="Port" leftLegend="No. Of Pax" digit={0} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
            lfBySector: [
                { component: <SortBarMultiLinesChart data={data.lfBySector} title={defaultTitle} bottomLegend="Sector" leftLegend="Load Factor (%)" digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxBySector} title={paxTitle} bottomLegend="Sector" leftLegend="No. Of Pax" digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
            lfBySectorType: [
                { component: <SortBarMultiLinesChart data={data.lfBySectorType} title={defaultTitle} bottomLegend="Sector Type" leftLegend="Load Factor (%)" isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxBySectorType} title={paxTitle} bottomLegend="Sector Type" leftLegend="No. Of Pax" isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
            lfByFlightType: [
                { component: <SortBarMultiLinesChart data={data.lfByFlightType} title={defaultTitle} bottomLegend="Flight Type" leftLegend="Load Factor (%)" isHorizontalBottomLegend={!isMobile ? true : undefined} digit={0} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByFlightType} title={paxTitle} bottomLegend="Flight Type" leftLegend="No. Of Pax" isHorizontalBottomLegend={!isMobile ? true : undefined} digit={0} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
            lfByFlightReg: [
                { component: <SortBarMultiLinesChart data={data.lfByFlightReg} title={defaultTitle} bottomLegend="Flight Reg" leftLegend="Load Factor (%)" digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByFlightReg} title={paxTitle} bottomLegend="Flight Reg" leftLegend="No. Of Pax" digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
            lfByACType: [
                { component: <SortBarMultiLinesChart data={data.lfByACType} title={defaultTitle} bottomLegend="AC Type" leftLegend="Load Factor (%)" digit={0} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByACType} title={paxTitle} bottomLegend="AC Type" leftLegend="No. Of Pax" digit={0} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
            lfByCountry: [
                { component: <SortBarMultiLinesChart data={data.lfByCountry} title={defaultTitle} bottomLegend="Country" leftLegend="Load Factor (%)" digit={0} bottomOffSet={25} bottomLegendOffSet={25} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByCountry} title={paxTitle} bottomLegend="Country" leftLegend="No. Of Pax" digit={0} bottomOffSet={25} bottomLegendOffSet={25} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
            lfByFlightNo: [
                { component: <SortBarMultiLinesChart data={data.lfByFlightNo} title={defaultTitle} bottomLegend="Flight No" leftLegend="Load Factor (%)" digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByFlightNo} title={paxTitle} bottomLegend="Flight No" leftLegend="No. Of Pax" digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={85} lineTranslateXOffSet={-10} />, style: {} },
            ],
        },
        tableau: {
            lfByDate: [
                { component: <LoadFactorTableau data={data.tableauLfByDate} /> },
            ],
            lfByDateDep: [
                { component: <LoadFactorTableau data={data.tableauLfByDateDep} /> },
            ],
            lfByDateSector: [
                { component: <LoadFactorTableau data={data.tableauLfByDateSector} /> },
            ],
            lfByDateSectorType: [
                { component: <LoadFactorTableau data={data.tableauLfByDateSectorType} /> },
            ],
            lfByDateFlightType: [
                { component: <LoadFactorTableau data={data.tableauLfByDateFlightType} /> },
            ],
            lfByDateFlightReg: [
                { component: <LoadFactorTableau data={data.tableauLfByDateFlightReg} /> },
            ],
            lfByDateACType: [
                { component: <LoadFactorTableau data={data.tableauLfByDateACType} /> },
            ],
            lfByDateCountry: [
                { component: <LoadFactorTableau data={data.tableauLfByDateCountry} /> },
            ],
            lfByDateFlightNo: [
                { component: <LoadFactorTableau data={data.tableauLfByDateFlightNo} /> },
            ],
            lfByDep: [
                { component: <LoadFactorTableau data={data.tableauLfByDep} /> },
            ],            
            lfBySector: [
                { component: <LoadFactorTableau data={data.tableauLfBySector} /> },
            ],
            lfBySectorType: [
                { component: <LoadFactorTableau data={data.tableauLfBySectorType} /> },
            ],
            lfByFlightType: [
                { component: <LoadFactorTableau data={data.tableauLfByFlightType} /> },
            ],            
            lfByFlightReg: [
                { component: <LoadFactorTableau data={data.tableauLfByFlightReg} /> },
            ],
            lfByFlightRegDep: [
                { component: <LoadFactorTableau data={data.tableauLfByFlightRegDep} /> },
            ],
            lfByFlightRegSector: [
                { component: <LoadFactorTableau data={data.tableauLfByFlightRegSector} /> },
            ],
            lfByFlightRegSectorType: [
                { component: <LoadFactorTableau data={data.tableauLfByFlightRegSectorType} /> },
            ],
            lfByFlightRegFlightType: [
                { component: <LoadFactorTableau data={data.tableauLfByFlightRegFlightType} /> },
            ],
            lfByFlightRegCountry: [
                { component: <LoadFactorTableau data={data.tableauLfByFlightRegCountry} /> },
            ],
            lfByACType: [
                { component: <LoadFactorTableau data={data.tableauLfByACType} /> },
            ],
            lfByACTypeDep: [
                { component: <LoadFactorTableau data={data.tableauLfByACTypeDep} /> },
            ],
            lfByACTypeSector: [
                { component: <LoadFactorTableau data={data.tableauLfByACTypeSector} /> },
            ],
            lfByACTypeSectorType: [
                { component: <LoadFactorTableau data={data.tableauLfByACTypeSectorType} /> },
            ],
            lfByACTypeFlightType: [
                { component: <LoadFactorTableau data={data.tableauLfByACTypeFlightType} /> },
            ],
            lfByACTypeCountry: [
                { component: <LoadFactorTableau data={data.tableauLfByACTypeCountry} /> },
            ],
            lfByCountry: [
                { component: <LoadFactorTableau data={data.tableauLfByCountry} /> },
            ],
            lfByFlightNo: [
                { component: <LoadFactorTableau data={data.tableauLfByFlightNo} /> },
            ],
        }  
    };   

    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }} 
        >
            {charts?.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width="100%"
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="Load Factor" 
                    subtitle={`${loading ? 'Loading...' : displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}`}
                /> 
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        {!isMobile && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Tooltip title="Minimum pax to count" arrow>
                                    <span>
                                        <NumberControl
                                            min={0}
                                            max={99999}
                                            value={minimumPax}
                                            onChange={(value) => {
                                                setMinimumPax(value); 
                                            }} 
                                            textFieldWidth={45}
                                        />
                                    </span>
                                </Tooltip>      

                                <FormControl
                                    size='small'
                                    sx={{  width: { xs: '6.8rem', sm: '8rem' } }}
                                >
                                    <InputLabel>Data Type</InputLabel>
                                    <Select
                                        label="Data Type"
                                        value={dataType}
                                        onChange={(e) => setDataType(e.target.value)}                                
                                    >
                                        <MenuItem value="actual">Actual</MenuItem>
                                        <MenuItem value="expected">Expected</MenuItem>
                                        <MenuItem value="noshow">Noshow</MenuItem>
                                    </Select>
                                </FormControl>                               
                            </Box>              
                        )}                

                        <FormControl 
                            size="small" 
                            sx={{ width: { xs: "6rem", sm: "8rem" } }}
                        >
                            <InputLabel>Display</InputLabel>
                            <Select
                                value={displayType}
                                label="Display"
                                onChange={(e) => setDisplayType(e.target.value)}
                            >
                                {displayOptions?.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl 
                            size="small" 
                            sx={{ width: { xs: "7rem", sm: "12rem" } }}
                        >
                            <InputLabel>View</InputLabel>
                            <Select
                                value={safeView} 
                                label="View"
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions[displayType]?.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}  
            </FlexBetween>                        
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    {displayType in chartMap && view in chartMap[displayType] && 
                        renderCharts(chartMap[displayType][view], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
  
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}  
            
            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }  
        </Box>
    );
};

export default LoadFactor;
